/**
 * Guardian community-report confirmation card state machine (2026-09-24,
 * agent-voice-auth). Pure helpers only — `useChatApp.js` wires these into
 * WebSocket events and send paths; no MUI/React lives here.
 *
 * The agent gates a proposed `incident_create_or_update` report behind a
 * card sent as a `confirmation_request` event. A card is only ever approved
 * by `confirmReport()` sending the literal text "Confirm" plus the card's
 * `confirmation_id` — typed text never approves it. Any new user send, a
 * socket reconnect, or expiry invalidates a still-pending card server-side;
 * these helpers keep the client's copy of that state in sync.
 *
 * Card states: 'pending' (awaiting the person's tap) -> 'submitting' (the
 * confirm payload is in flight) -> 'resolved' (agent replied, success or
 * failure — the outcome is its own text/error message) or 'stale' (no
 * longer confirmable; ask again). 'unknown' means the connection dropped while
 * the confirm was in flight: the report may or may not have been filed, so the
 * card never offers a retry. 'unsupported' (plan 05 B3, 2026-09-29) is
 * terminal: the frame named a tool/version this client cannot display, so it
 * was never confirmable and nothing was submitted.
 */

import { nextMessageId } from './messageIds';
import {
  CARD_KIND_INCIDENT_REPORT,
  CARD_KIND_UNSUPPORTED,
  parseConfirmationRequest,
  parseLegacyIncidentProposal,
} from './confirmationRequest';

/** True for a chat message that renders as a confirmation card. */
function isConfirmationMessage(message) {
  return Boolean(message) && message.type === 'confirmation';
}

/**
 * Card kind of a thread message (plan 05 B3, 2026-09-29). Cards saved before
 * B3 carry no `cardKind` and were only ever incident reports; any other
 * unrecognised kind is 'unsupported' and can never be approved.
 * @returns {string|null} null for a non-card message.
 */
export function resolveCardKind(message) {
  if (!isConfirmationMessage(message)) return null;
  const kind = message.cardKind === undefined ? CARD_KIND_INCIDENT_REPORT : message.cardKind;
  return kind === CARD_KIND_INCIDENT_REPORT ? kind : CARD_KIND_UNSUPPORTED;
}

/**
 * Card kind to render. Additionally shows a restored incident card whose
 * fields no longer pass the incident rules (edited storage) as
 * 'unsupported' instead of crashing the thread on render.
 */
export function resolveRenderCardKind(message) {
  const kind = resolveCardKind(message);
  if (kind === CARD_KIND_INCIDENT_REPORT && !parseLegacyIncidentProposal(message.proposed)) {
    return CARD_KIND_UNSUPPORTED;
  }
  return kind;
}

/** Only a displayable card kind can ever be approved (plan 05 B3, 2026-09-29). */
export function isConfirmableCard(message) {
  return resolveCardKind(message) === CARD_KIND_INCIDENT_REPORT;
}

/**
 * Builds the in-memory card message from a `confirmation_request` WebSocket
 * event. External bytes are never trusted: returns null (caller should drop
 * the event) when there is no usable id or a legacy frame is malformed. A
 * versioned frame this client cannot display becomes an 'unsupported' card
 * (terminal state 'unsupported', no proposal kept) instead of being dropped
 * (plan 05 B3, 2026-09-29; parsing lives in ./confirmationRequest).
 * @param {object} data - Parsed WebSocket payload.
 * @returns {object|null}
 */
export function buildConfirmationCardMessage(data) {
  const parsed = parseConfirmationRequest(data);
  if (!parsed) {
    // A persisted card with an object where text belongs would crash the
    // thread on every reload; drop malformed events instead.
    return null;
  }
  const base = {
    id: nextMessageId(),
    type: 'confirmation',
    cardKind: parsed.cardKind,
    schemaVersion: parsed.schemaVersion,
    confirmationId: parsed.confirmationId,
    tool: parsed.tool,
    timestamp: new Date().toLocaleTimeString(),
  };
  if (parsed.cardKind === CARD_KIND_UNSUPPORTED) {
    // Nothing from the proposal is kept or persisted; the card never
    // enters the pending -> submitting path.
    return { ...base, expiresAt: Date.now(), state: 'unsupported' };
  }
  const expiresInSeconds = Number(data.expires_in_seconds);
  const ttlMs = Number.isFinite(expiresInSeconds) && expiresInSeconds > 0
    ? expiresInSeconds * 1000
    : 0;
  return {
    ...base,
    // Only the rendered fields, never the whole server object: it is
    // persisted to localStorage on every save (2026-09-25 pass 20).
    proposed: parsed.proposed,
    expiresAt: Date.now() + ttlMs,
    state: 'pending',
  };
}

/** Marks every pending card stale — a new user send or a socket reconnect. */
export function staleAllPendingCards(messages) {
  return messages.map((m) => (
    isConfirmationMessage(m) && m.state === 'pending' ? { ...m, state: 'stale' } : m
  ));
}

/**
 * The socket that would carry (or was carrying) an approval is gone: pending
 * cards go stale, and a card mid-submit becomes 'unknown' because the server
 * may already have executed it and a new socket cannot deduplicate a retry.
 */
export function markSocketLost(messages) {
  return messages.map((m) => {
    if (!isConfirmationMessage(m)) return m;
    if (m.state === 'pending') return { ...m, state: 'stale' };
    if (m.state === 'submitting') return { ...m, state: 'unknown' };
    return m;
  });
}

/**
 * The agent refused the confirm turn at sign-in, before running anything:
 * the submitting card definitely did not execute, so it is stale (ask
 * again), never 'unknown'.
 */
export function staleSubmittingCards(messages) {
  return messages.map((m) => (
    isConfirmationMessage(m) && m.state === 'submitting' ? { ...m, state: 'stale' } : m
  ));
}

/**
 * Marks one specific pending card stale (used for expiry, where only that
 * card's timer fired — a different card may have since taken its place).
 */
export function staleCardIfPending(messages, confirmationId) {
  return messages.map((m) => (
    isConfirmationMessage(m) && m.confirmationId === confirmationId && m.state === 'pending'
      ? { ...m, state: 'stale' }
      : m
  ));
}

/**
 * Transitions the confirmed card to 'submitting'. Confirming is itself a new
 * send, so any other still-pending card (there should normally be at most
 * one) goes stale in the same pass.
 */
export function markCardSubmitting(messages, confirmationId) {
  return messages.map((m) => {
    if (!isConfirmationMessage(m)) return m;
    // Never move a non-confirmable card into the submit path (B3, 2026-09-29).
    if (m.confirmationId === confirmationId) {
      return isConfirmableCard(m) ? { ...m, state: 'submitting' } : m;
    }
    if (m.state === 'pending') return { ...m, state: 'stale' };
    return m;
  });
}

/** Reverts an optimistic 'submitting' card back to 'pending' if the send itself throws. */
export function revertCardToPending(messages, confirmationId) {
  return messages.map((m) => (
    isConfirmableCard(m) && m.confirmationId === confirmationId
      ? { ...m, state: 'pending' }
      : m
  ));
}

/** Resolves the submitting card once the agent's `done` event arrives (success or failure). */
export function markCardResolved(messages, confirmationId) {
  return messages.map((m) => (
    isConfirmableCard(m) && m.confirmationId === confirmationId
      ? { ...m, state: 'resolved' }
      : m
  ));
}

/**
 * Prepares cards for localStorage so a reload never resurrects a confirmable
 * card: 'pending' is stored as 'stale' (ask again), and 'submitting' as
 * 'unknown' — the server may already have filed it, so the stored card must
 * never invite a retry. Called on every persist; the in-memory copy is
 * untouched.
 */
export function sanitizeMessagesForPersist(messages) {
  return messages.map((m) => {
    if (!isConfirmationMessage(m)) return m;
    if (m.state === 'pending') return { ...m, state: 'stale' };
    if (m.state === 'submitting') return { ...m, state: 'unknown' };
    return m;
  });
}

/**
 * Finds a confirmation card by its server-issued confirmation id. Returns
 * null when no card, or more than one card, carries the id, so an ambiguous
 * approval fails closed (review B3 F3, 2026-09-29).
 */
export function findConfirmationCard(messages, confirmationId) {
  const matches = messages.filter(
    (m) => isConfirmationMessage(m) && m.confirmationId === confirmationId,
  );
  return matches.length === 1 ? matches[0] : null;
}
